import React from 'react';
import {
  Paper,
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  makeStyles,
  useTheme,
  Theme,
  Chip,
} from '@material-ui/core/';
import { BarChart, Bar, Tooltip, YAxis, XAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import Skeleton from '@material-ui/lab/Skeleton';
import { DataType, FiltersType } from '../types';

type Props = {
  data: DataType | null;
  isLoading: boolean;
  filters: FiltersType | null;
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: 20,
  },
  paperContainer: {
    padding: theme.spacing(3),
  },
  section: {
    marginTop: theme.spacing(2),
  },
}));

export default function ChartContainer({ data, isLoading, filters }: Props) {
  const classes = useStyles();
  const theme = useTheme();

  const renderTooltip = (props: any) => {
    if (typeof props.payload != 'undefined' && props.payload != null) {
      if (typeof props.payload[0] != 'undefined') {
        const formatter = new Intl.NumberFormat('da-DK');
        return (
          <Card style={{ zIndex: 1000 }}>
            <CardContent>
              <Typography variant="subtitle1" color="primary">
                <b>{props.payload[0].payload.timeGrp}</b>
              </Typography>
              <Divider />
              <Typography variant="subtitle2" style={{ color: theme.clicks.primary, marginTop: theme.spacing(2) }}>
                <Chip
                  size="small"
                  label="0"
                  style={{ backgroundColor: theme.clicks.primary, color: theme.clicks.primary, marginRight: 10 }}
                />
                Pending no payment received: {formatter.format(props.payload[0].payload.sums.pendingNoPaymentReceived)}{' '}
                {props.payload[0].payload.currencyCode}
              </Typography>
              <Typography variant="subtitle2" style={{ color: theme.conversions.primary, marginTop: theme.spacing(2) }}>
                <Chip
                  size="small"
                  label="0"
                  style={{
                    backgroundColor: theme.conversions.primary,
                    color: theme.conversions.primary,
                    marginRight: 10,
                  }}
                />
                Pending Payment Received: {formatter.format(props.payload[0].payload.sums.pendingPaymentReceived)}{' '}
                {props.payload[0].payload.currencyCode}
              </Typography>
              <Typography
                variant="subtitle2"
                style={{ color: theme.partnerCommission.primary, marginTop: theme.spacing(2) }}
              >
                <Chip
                  size="small"
                  label="0"
                  style={{
                    backgroundColor: theme.partnerCommission.primary,
                    color: theme.partnerCommission.primary,
                    marginRight: 10,
                  }}
                />
                Rejected: {formatter.format(props.payload[0].payload.sums.rejected)}{' '}
                {props.payload[0].payload.currencyCode}
              </Typography>
              <Typography variant="subtitle2" style={{ color: theme.commission.primary, marginTop: theme.spacing(2) }}>
                <Chip
                  size="small"
                  label="0"
                  style={{
                    backgroundColor: theme.commission.primary,
                    color: theme.commission.primary,
                    marginRight: 10,
                  }}
                />
                Paid: {formatter.format(props.payload[0].payload.sums.paid)} {props.payload[0].payload.currencyCode}
              </Typography>
              <Divider />
              <Typography variant="subtitle1" color="primary" style={{ marginTop: theme.spacing(2) }}>
                Total Conversions: <b>{formatter.format(props.payload[0].payload.counts.conversionsCount)}</b>
              </Typography>
            </CardContent>
          </Card>
        );
      }
    }
  };

  const renderLabel = (props: any, rows: any) => {
    return (
      <text
        x={props.x}
        y={props.y + 10}
        dy={10}
        fill="#282E67"
        fontSize={12}
        fontFamily="Rubik"
        fontWeight="500"
        textAnchor="middle"
      >
        {props.payload.value}
      </text>
    );
  };

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12}>
        <Paper className={classes.paperContainer} variant="outlined">
          <Grid container>
            <Grid item xs={8}>
              <Grid container spacing={3}>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="primary">
                    <Chip
                      size="small"
                      label="0"
                      style={{ backgroundColor: theme.clicks.primary, color: theme.clicks.primary, marginRight: 10 }}
                    />
                    Pending no payment received
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="primary">
                    <Chip
                      size="small"
                      label="0"
                      style={{
                        backgroundColor: theme.conversions.primary,
                        color: theme.conversions.primary,
                        marginRight: 10,
                      }}
                    />
                    Pending payment received
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="subtitle2" color="primary">
                    <Chip
                      size="small"
                      label="0"
                      style={{
                        backgroundColor: theme.partnerCommission.primary,
                        color: theme.partnerCommission.primary,
                        marginRight: 10,
                      }}
                    />
                    Rejected
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="subtitle2" color="primary">
                    <Chip
                      size="small"
                      label="0"
                      style={{
                        backgroundColor: theme.commission.primary,
                        color: theme.commission.primary,
                        marginRight: 10,
                      }}
                    />
                    Paid
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container className={classes.section}>
            {isLoading ? (
              <Skeleton variant="rect" width="100%" height={300} animation="wave" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={data?.bars}
                  margin={{
                    top: 7,
                    right: 7,
                    left: 7,
                    bottom: 1,
                  }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="5 3" />
                  <XAxis
                    dataKey="timeGrp"
                    tick={(props: any) => renderLabel(props, data)}
                    stroke={theme.palette.primary.main}
                    strokeOpacity={0.2}
                  />
                  <YAxis hide />
                  <Tooltip cursor={{ fill: 'rgba(0, 0, 150, 0.024)' }} content={(props: any) => renderTooltip(props)} />
                  <Bar
                    dataKey="bar.paid"
                    fill={theme.commission.primary}
                    stroke={theme.commission.primary}
                    strokeWidth={0.9}
                    fillOpacity={0.9}
                    strokeOpacity={0.6}
                    stackId="a"
                    barSize={80}
                    maxBarSize={100}
                  />
                  <Bar
                    dataKey="bar.rejected"
                    fill={theme.partnerCommission.primary}
                    stroke={theme.partnerCommission.primary}
                    strokeWidth={0.9}
                    fillOpacity={0.9}
                    strokeOpacity={0.6}
                    stackId="a"
                    barSize={80}
                    maxBarSize={100}
                  />
                  <Bar
                    dataKey="bar.pendingPaymentReceived"
                    fill={theme.conversions.primary}
                    stroke={theme.conversions.primary}
                    strokeWidth={0.9}
                    fillOpacity={0.9}
                    strokeOpacity={0.6}
                    stackId="a"
                    barSize={80}
                    maxBarSize={100}
                  />
                  <Bar
                    dataKey="bar.pendingNoPaymentReceived"
                    fill={theme.clicks.primary}
                    stroke={theme.clicks.primary}
                    strokeWidth={0.9}
                    fillOpacity={0.9}
                    strokeOpacity={0.6}
                    stackId="a"
                    barSize={80}
                    maxBarSize={100}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}
